import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { D, designError, fmtDate, fmtDateTime, label, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, FilesPanel, NoAccess, PageHeader, PriorityTag, Section, SimpleSelect, StatusBadge, useQueryId,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, Search, Send, UserPlus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", request_type: "new_product", priority: "medium", due_date: "", customer_name: "", sales_ref: "", department: "" };

function useDesignEngineers(enabled) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get(`${D}/settings`).then((r) => setList(r.data.users.filter((u) => ["design_engineer", "checker", "design_head", "admin"].includes(u.functional_role)))).catch(() => setList([]));
  }, [enabled]);
  return list;
}

function RequestDetail({ id, onChanged }) {
  const { can } = useDesignMe();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [assignee, setAssignee] = useState("");
  const [assignDue, setAssignDue] = useState("");
  const [comment, setComment] = useState("");
  const [statusTo, setStatusTo] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const engineers = useDesignEngineers(can("request_manage") && can("settings"));
  const load = useCallback(() => api.get(`${D}/requests/${id}`).then((r) => { setRec(r.data); setAssignDue(r.data.due_date || ""); }).catch((e) => toast.error(designError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!rec) return null;
  const changed = () => { load(); onChanged?.(); };
  const act = async (fn) => { try { await fn(); changed(); } catch (e) { toast.error(designError(e)); } };
  const open = ["new", "assigned", "in_progress", "on_hold"].includes(rec.status);
  const nextStatuses = {
    new: ["rejected", "cancelled"], assigned: ["in_progress", "on_hold", "rejected", "cancelled"], in_progress: ["on_hold", "completed", "cancelled"],
    on_hold: ["in_progress", "cancelled"], completed: ["closed", "in_progress"],
  }[rec.status] || [];
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number} · {label(rec.request_type)} · raised {fmtDate(rec.created_at)} by {rec.requested_by_name || rec.requested_by}</div>
        <div className="text-xl font-bold text-slate-900">{rec.title}</div>
        <div className="flex gap-3 items-center mt-1 text-sm max-md:flex-wrap max-md:gap-x-3 max-md:gap-y-1">
          <StatusBadge status={rec.status} /> <PriorityTag priority={rec.priority} />
          <span className={rec.overdue ? "text-red-600 font-semibold" : "text-slate-600"}>Due {fmtDate(rec.due_date)}{rec.overdue ? " · OVERDUE" : ""}</span>
          <span className="text-slate-500">Age {rec.age_days} d</span>
          <span className="text-slate-600">Assignee: <b>{rec.assignee_name || rec.assignee_email || "—"}</b></span>
        </div>
      </div>
      <Section title="Request">
        <div className="text-sm whitespace-pre-wrap">{rec.description || "—"}</div>
        <div className="grid sm:grid-cols-3 gap-2 text-xs text-slate-600 mt-3">
          <div>Customer: <b>{rec.customer_name || "—"}</b></div><div>Sales ref: <b>{rec.sales_ref || "—"}</b></div><div>Department: <b>{rec.department || label(rec.requested_by_role)}</b></div>
        </div>
      </Section>
      {can("request_manage") && open && (
        <Section title="Assign">
          <div className="flex gap-2 items-end flex-wrap max-md:[&>*]:w-full">
            <Field label="Engineer">
              {engineers.length ? <div className="w-64 max-md:w-full"><SimpleSelect value={assignee} onChange={setAssignee} options={engineers.map((u) => ({ value: u.email, label: `${u.name || u.email} (${label(u.functional_role)})` }))} /></div>
                : <Input className="w-64 max-md:w-full" placeholder="engineer@company.com" value={assignee} onChange={(e) => setAssignee(e.target.value)} />}
            </Field>
            <Field label="Due date"><Input type="date" value={assignDue} onChange={(e) => setAssignDue(e.target.value)} /></Field>
            <Button disabled={!assignee} onClick={() => act(async () => { await api.post(`${D}/requests/${id}/assign`, { assignee_email: assignee, due_date: assignDue || null }); toast.success("Assigned"); })} data-testid="req-assign">
              <UserPlus className="w-4 h-4 mr-1" />Assign
            </Button>
          </div>
        </Section>
      )}
      {nextStatuses.length > 0 && (
        <Section title="Update status">
          <div className="flex gap-2 items-end flex-wrap">
            <div className="w-48 max-md:w-full"><SimpleSelect value={statusTo} onChange={setStatusTo} options={nextStatuses} testid="req-status-select" /></div>
            <Input className="flex-1 min-w-[16rem] max-md:min-w-0 max-md:basis-full" placeholder="Comment (required for hold / reject / cancel)" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} />
            <Button disabled={!statusTo} onClick={() => act(async () => { await api.post(`${D}/requests/${id}/status`, { status: statusTo, comment: statusNote }); setStatusTo(""); setStatusNote(""); toast.success("Updated"); })} data-testid="req-status-apply">Apply</Button>
          </div>
        </Section>
      )}
      <div className="flex gap-2 flex-wrap">
        {can("create_edit") && open && <Button size="sm" variant="outline" onClick={() => nav(`/design/items?new=1`)}>Create item for this request</Button>}
        {can("npd_manage") && rec.request_type === "new_product" && !rec.npd_project_id && (
          <Button size="sm" variant="outline" onClick={() => act(async () => {
            const { data } = await api.post(`${D}/npd`, { name: rec.title, customer_name: rec.customer_name, request_id: rec.id, target_date: rec.due_date });
            toast.success(`${data.number} created`);
          })}>Start NPD project</Button>
        )}
        {rec.npd_number && <span className="text-sm text-slate-600">NPD: <b>{rec.npd_number}</b></span>}
      </div>
      <FilesPanel ownerType="requests" ownerId={rec.id} files={rec.files} editable={open} onChanged={load} title="Attachments" />
      <Section title={`Discussion (${rec.comments?.length || 0})`}>
        <div className="space-y-2 max-h-64 overflow-auto">
          {(rec.comments || []).map((c) => (
            <div key={c.id} className="text-sm"><b>{c.by_name || c.by}</b> <span className="text-xs text-slate-500">{fmtDateTime(c.at)}</span><div className="whitespace-pre-wrap text-slate-700">{c.text}</div></div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" />
          <Button disabled={!comment.trim()} onClick={() => act(async () => { await api.post(`${D}/requests/${id}/comments`, { text: comment }); setComment(""); })}><Send className="w-4 h-4" /></Button>
        </div>
      </Section>
      <Section title="History">
        {(rec.history || []).map((h, i) => <div key={i} className="text-xs text-slate-600">{fmtDateTime(h.at)} · {label(h.action)} {h.from ? `${label(h.from)} → ` : ""}{label(h.to)} · {h.by} {h.comment && `· ${h.comment}`}</div>)}
      </Section>
    </div>
  );
}

export default function DesignRequests() {
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [mine, setMine] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    const st = status === "open" ? "new,assigned,in_progress,on_hold" : status === "all" ? "" : status;
    api.get(`${D}/requests`, { params: { status: st, mine, overdue } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [status, mine, overdue]);
  useEffect(load, [load]);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const shown = rows.filter((r) => !q || `${r.number} ${r.title} ${r.customer_name}`.toLowerCase().includes(q.toLowerCase()));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const create = async () => {
    setErrors([]);
    try {
      const { data } = await api.post(`${D}/requests`, { ...form, due_date: form.due_date || null });
      toast.success(`${data.number} raised`);
      setCreating(false); setForm(EMPTY); load(); setOpenId(data.id);
    } catch (e) { setErrors([designError(e)]); }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-requests-page">
      <PageHeader title="Design Requests" subtitle="Intake from Sales, Production, QC and others — assignment, priority, due dates and workflow">
        {can("request_create") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="design-new-request"><Plus className="w-4 h-4 mr-1.5" />New request</Button>}
      </PageHeader>
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative w-64 max-md:w-full"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="w-44 max-md:flex-1 max-md:min-w-[8rem]"><SimpleSelect value={status} onChange={setStatus} options={[{ value: "open", label: "Open" }, { value: "all", label: "All" }, ...(me?.enums?.request_statuses || []).map((s) => ({ value: s, label: label(s) }))]} /></div>
        <label className="text-sm flex items-center gap-2"><Checkbox checked={mine} onCheckedChange={(v) => setMine(!!v)} />Mine</label>
        <label className="text-sm flex items-center gap-2"><Checkbox checked={overdue} onCheckedChange={(v) => setOverdue(!!v)} />Overdue only</label>
      </div>
      <DataTable rows={shown} onRowClick={(r) => setOpenId(r.id)} testid="design-requests-table"
        columns={[
          { key: "number", title: "Request", cellClass: "mono font-semibold" },
          { key: "title", title: "Title", cellClass: "max-w-xs truncate" },
          { key: "request_type", title: "Type", render: (r) => label(r.request_type) },
          { key: "priority", title: "Priority", render: (r) => <PriorityTag priority={r.priority} /> },
          { key: "requested_by", title: "Requested by", render: (r) => r.requested_by_name || r.requested_by },
          { key: "assignee", title: "Assignee", render: (r) => r.assignee_name || r.assignee_email || <span className="text-slate-400">unassigned</span> },
          { key: "due_date", title: "Due", render: (r) => <span className={r.overdue ? "text-red-600 font-semibold" : ""}>{fmtDate(r.due_date)}</span> },
          { key: "age_days", title: "Age (d)", cellClass: "mono" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>New design request</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Title *" className="sm:col-span-2"><Input value={form.title} onChange={set("title")} data-testid="req-title" /></Field>
            <Field label="Type"><SimpleSelect value={form.request_type} onChange={set("request_type")} options={me?.enums?.request_types || []} /></Field>
            <Field label="Priority"><SimpleSelect value={form.priority} onChange={set("priority")} options={me?.enums?.priorities || []} /></Field>
            <Field label="Required by"><Input type="date" value={form.due_date} onChange={set("due_date")} /></Field>
            <Field label="Customer"><Input value={form.customer_name} onChange={set("customer_name")} /></Field>
            <Field label="Sales / enquiry ref."><Input value={form.sales_ref} onChange={set("sales_ref")} /></Field>
            <Field label="Department"><Input value={form.department} onChange={set("department")} /></Field>
            <Field label="Description" className="sm:col-span-2"><Textarea rows={4} value={form.description} onChange={set("description")} /></Field>
          </div>
          <div className="text-xs text-slate-500">Attachments can be added once the request is created.</div>
          <ErrorList errors={errors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={form.title.length < 3} onClick={create} data-testid="req-create">Raise request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader><SheetTitle>Design request</SheetTitle></SheetHeader>
          {openId && <RequestDetail key={openId} id={openId} onChanged={load} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
